import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import { apiService, SensorField } from '../../services/ApiService';
import { useSondeContext } from '../../context/SondeContext';

// Types pour les conditions multiples
interface RelayCondition {
  field: string;
  operator: string;
  setpoint: number;
  hysteresis: number;
  group: number;
}

interface RelayFormData {
  name: string;
  is_enabled: boolean;
  field_code: string;
  activate_below_setpoint: boolean;
  setpoint: number;
  hysteresis: number;
  min_on_time: number;
  min_off_time: number;
  max_on_time: number | null;
  delay_before_start: number;
  invert_output: boolean;
  logic_mode: 'SIMPLE' | 'AND' | 'OR' | 'GROUPS';
  conditions: RelayCondition[];
}

interface BoitierRelays {
  boitier_id: number;
  fields: SensorField[];
  relay1: RelayFormData;
  relay2: RelayFormData;
  relay1_state: boolean;
  relay2_state: boolean;
}

const defaultRelayConfig: RelayFormData = {
  name: '',
  is_enabled: false,
  field_code: '',
  activate_below_setpoint: true,
  setpoint: 25,
  hysteresis: 0.5,
  min_on_time: 0,
  min_off_time: 0,
  max_on_time: null,
  delay_before_start: 0,
  invert_output: false,
  logic_mode: 'AND',
  conditions: [],
};

const RegulationView: React.FC = () => {
  const { bains: boitiers } = useSondeContext();
  
  const [relayConfigs, setRelayConfigs] = useState<BoitierRelays[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [expanded, setExpanded] = useState<number | false>(false);

  const handleAccordionChange = (boitierId: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? boitierId : false);
  };

  // Charger les configurations
  useEffect(() => {
    const fetchConfigs = async () => {
      if (boitiers.length === 0) {
        setLoading(false);
        return;
      }

      // Convertir le format SIMPLE vers conditions[]
          const parseConditions = (data: any): RelayCondition[] => {
            // Si conditions_json existe, l'utiliser
            if (data.conditions_json) {
              try {
                const parsed = JSON.parse(data.conditions_json);
                // Format groups: {groups:[{conditions:[...]}]}
                if (parsed && typeof parsed === 'object' && Array.isArray(parsed.groups)) {
                  const out: RelayCondition[] = [];
                  for (const g of parsed.groups) {
                    const groupId = Number(g?.id ?? g?.group ?? 1) || 1;
                    const conds = Array.isArray(g?.conditions) ? g.conditions : [];
                    for (const c of conds) {
                      out.push({
                        field: c?.field,
                        operator: c?.operator ?? '<',
                        setpoint: c?.setpoint ?? 25,
                        hysteresis: c?.hysteresis ?? 0.5,
                        group: Number(c?.group ?? groupId) || groupId,
                      });
                    }
                  }
                  return out.filter(c => !!c.field);
                }
                // Format liste de conditions
                if (Array.isArray(parsed)) {
                  return parsed.map((c: any) => ({
                    field: c?.field,
                    operator: c?.operator ?? '<',
                    setpoint: c?.setpoint ?? 25,
                    hysteresis: c?.hysteresis ?? 0.5,
                    group: Number(c?.group ?? 1) || 1,
                  })).filter((c: any) => !!c.field);
                }
                return [];
              } catch { return []; }
            }
            // Migration depuis format field_code/setpoint
            if (data.field_code) {
              return [{
                field: data.field_code,
                operator: data.activate_below_setpoint ? '<' : '>',
                setpoint: data.setpoint ?? 25,
                hysteresis: data.hysteresis ?? 0.5,
                group: 1,
              }];
            }
            return [];
          };

      try {
        setLoading(true);

        // Charger tous les boitiers en parallèle (fields + relays simultanément)
        const results = await Promise.all(
          boitiers.map(async (boitier) => {
            const boitierId = boitier.boitier_id;
            const [fields, relayData] = await Promise.all([
              apiService.getDeviceFields(boitierId),
              apiService.getRelayConfigs(boitierId),
            ]);
            return { boitierId, fields, relayData };
          })
        );

        const configs: BoitierRelays[] = results.map(({ boitierId, fields, relayData }) => {
          const relay1Data = relayData.find((r: any) => r.relay_number === 1) || {};
          const relay2Data = relayData.find((r: any) => r.relay_number === 2) || {};
          return {
            boitier_id: boitierId,
            fields: fields,
            relay1: {
              name: relay1Data.name || 'Relais 1',
              is_enabled: Boolean(relay1Data.is_enabled),
              field_code: relay1Data.field_code || '',
              activate_below_setpoint: relay1Data.activate_below_setpoint ?? true,
              setpoint: relay1Data.setpoint ?? 25,
              hysteresis: relay1Data.hysteresis ?? 0.5,
              min_on_time: relay1Data.min_on_time ?? 0,
              min_off_time: relay1Data.min_off_time ?? 0,
              max_on_time: relay1Data.max_on_time ?? null,
              delay_before_start: relay1Data.delay_before_start ?? 0,
              invert_output: Boolean(relay1Data.invert_output),
              logic_mode: relay1Data.logic_mode || 'AND',
              conditions: parseConditions(relay1Data),
            },
            relay2: {
              name: relay2Data.name || 'Relais 2',
              is_enabled: Boolean(relay2Data.is_enabled),
              field_code: relay2Data.field_code || '',
              activate_below_setpoint: relay2Data.activate_below_setpoint ?? true,
              setpoint: relay2Data.setpoint ?? 25,
              hysteresis: relay2Data.hysteresis ?? 0.5,
              min_on_time: relay2Data.min_on_time ?? 0,
              min_off_time: relay2Data.min_off_time ?? 0,
              max_on_time: relay2Data.max_on_time ?? null,
              delay_before_start: relay2Data.delay_before_start ?? 0,
              invert_output: Boolean(relay2Data.invert_output),
              logic_mode: relay2Data.logic_mode || 'AND',
              conditions: parseConditions(relay2Data),
            },
            relay1_state: Boolean(relay1Data.current_state),
            relay2_state: Boolean(relay2Data.current_state),
          };
        });

        configs.sort((a, b) => a.boitier_id - b.boitier_id);
        setRelayConfigs(configs);
        setError(null);
      } catch (err: any) {
        console.error('Erreur lors du chargement des configurations:', err);
        setError(`Impossible de charger les configurations : ${err.message || 'Erreur inconnue'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, [boitiers]);

  // Polling des états relais toutes les 10 secondes
  useEffect(() => {
    if (relayConfigs.length === 0) return;

    const pollRelayStates = async () => {
      try {
        const results = await Promise.all(
          relayConfigs.map(async (config) => {
            const relayData = await apiService.getRelayConfigs(config.boitier_id);
            const r1 = relayData.find((r: any) => r.relay_number === 1);
            const r2 = relayData.find((r: any) => r.relay_number === 2);
            return {
              boitier_id: config.boitier_id,
              relay1_state: Boolean(r1?.current_state),
              relay2_state: Boolean(r2?.current_state),
            };
          })
        );
        setRelayConfigs(prev => prev.map(config => {
          const updated = results.find(r => r.boitier_id === config.boitier_id);
          if (updated) {
            return { ...config, relay1_state: updated.relay1_state, relay2_state: updated.relay2_state };
          }
          return config;
        }));
      } catch (err) {
        // Silencieux — simple polling de rafraîchissement
      }
    };

    const intervalId = setInterval(pollRelayStates, 10000);
    return () => clearInterval(intervalId);
  }, [relayConfigs.length]);

  // Mettre à jour un champ de config
  const handleConfigChange = (
    boitierId: number, 
    relayNum: 1 | 2, 
    field: keyof RelayFormData, 
    value: any
  ) => {
    setRelayConfigs(prev => prev.map(config => {
      if (config.boitier_id === boitierId) {
        const relayKey = relayNum === 1 ? 'relay1' : 'relay2';
        return {
          ...config,
          [relayKey]: {
            ...config[relayKey],
            [field]: value
          }
        };
      }
      return config;
    }));
  };

  // Sauvegarder la configuration d'un relais
  const handleSave = async (boitierId: number, relayNum: 1 | 2) => {
    const saveKey = `${boitierId}-${relayNum}`;
    setSaving(prev => ({ ...prev, [saveKey]: true }));

    try {
      const config = relayConfigs.find(c => c.boitier_id === boitierId);
      if (!config) throw new Error('Configuration non trouvée');

      const relayData = relayNum === 1 ? config.relay1 : config.relay2;

      // Validation : au moins une condition cochée
      if (relayData.is_enabled && relayData.conditions.length === 0) {
        throw new Error('Veuillez cocher au moins une grandeur à surveiller');
      }

      // Construire le format "groupes" => (AND dans un groupe) OR (entre groupes)
      const groupsMap = new Map<number, RelayCondition[]>();
      for (const c of relayData.conditions) {
        const g = Number(c.group ?? 1) || 1;
        if (!groupsMap.has(g)) groupsMap.set(g, []);
        groupsMap.get(g)!.push(c);
      }
      const groups = Array.from(groupsMap.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([gid, conds]) => ({
          id: gid,
          conditions: conds.map(({ field, operator, setpoint, hysteresis }) => ({ field, operator, setpoint, hysteresis })),
        }));

      const conditionsPayload = {
        group_operator: 'OR',
        groups,
      };

      const success = await apiService.updateRelayConfig(boitierId, relayNum, {
        name: relayData.name,
        is_enabled: relayData.is_enabled,
        field_code: relayData.conditions[0]?.field || null, // Compat/affichage
        activate_below_setpoint: relayData.conditions[0]?.operator === '<' || relayData.conditions[0]?.operator === '<=',
        setpoint: relayData.conditions[0]?.setpoint ?? relayData.setpoint,
        hysteresis: relayData.conditions[0]?.hysteresis ?? relayData.hysteresis,
        min_on_time: relayData.min_on_time,
        min_off_time: relayData.min_off_time,
        max_on_time: relayData.max_on_time,
        delay_before_start: relayData.delay_before_start,
        invert_output: relayData.invert_output,
        logic_mode: 'GROUPS',
        conditions_json: JSON.stringify(conditionsPayload),
      });

      if (success) {
        setSnackbar({
          open: true,
          message: `Configuration du ${relayData.name} sauvegardée avec succès`,
          severity: 'success'
        });
      } else {
        throw new Error('Échec de la sauvegarde');
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Erreur lors de la sauvegarde',
        severity: 'error'
      });
    } finally {
      setSaving(prev => ({ ...prev, [saveKey]: false }));
    }
  };

  // Forcer l'état d'un relais manuellement
  const handleForceState = async (boitierId: number, relayNum: 1 | 2, newState: boolean) => {
    try {
      const success = await apiService.setRelayState(boitierId, relayNum, newState, 'Commande manuelle IHM');
      
      if (success) {
        // Mettre à jour l'état local
        setRelayConfigs(prev => prev.map(config => {
          if (config.boitier_id === boitierId) {
            const stateKey = relayNum === 1 ? 'relay1_state' : 'relay2_state';
            return { ...config, [stateKey]: newState };
          }
          return config;
        }));

        setSnackbar({
          open: true,
          message: `Relais ${relayNum} ${newState ? 'activé' : 'désactivé'} manuellement`,
          severity: 'info'
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Erreur lors du changement d\'état',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Rendu d'un panneau de configuration de relais
  const renderRelayPanel = (boitierId: number, relayNum: 1 | 2, relay: RelayFormData, currentState: boolean, fields: SensorField[]) => {
    const saveKey = `${boitierId}-${relayNum}`;
    const isSaving = saving[saveKey] || false;

    return (
      <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsInputComponentIcon color={relay.is_enabled ? 'primary' : 'disabled'} />
            <TextField
              size="small"
              label="Nom du relais"
              value={relay.name}
              onChange={(e) => handleConfigChange(boitierId, relayNum, 'name', e.target.value)}
              sx={{ width: 200 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              icon={<PowerSettingsNewIcon />}
              label={currentState ? 'ON' : 'OFF'}
              color={currentState ? 'success' : 'default'}
              variant={currentState ? 'filled' : 'outlined'}
            />
            <Tooltip title={currentState ? 'Forcer OFF' : 'Forcer ON'}>
              <IconButton 
                size="small" 
                onClick={() => handleForceState(boitierId, relayNum, !currentState)}
                color={currentState ? 'error' : 'success'}
              >
                <PowerSettingsNewIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={relay.is_enabled}
              onChange={(e) => handleConfigChange(boitierId, relayNum, 'is_enabled', e.target.checked)}
              color="primary"
            />
          }
          label={<Typography fontWeight="bold">Activer la régulation automatique</Typography>}
        />

        {relay.is_enabled && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Tableau des grandeurs - toujours utiliser conditions[] */}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Cochez les grandeurs à surveiller et configurez leurs seuils :
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell padding="checkbox" sx={{ width: 50, textAlign: 'center' }}>
                          <Typography variant="caption" fontWeight="bold">Actif</Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Grandeur</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 70 }}>Groupe</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 110 }}>Condition</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Consigne</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 90 }}>Hystérésis</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field) => {
                        const conditionIndex = relay.conditions.findIndex(c => c.field === field.field_code);
                        const isSelected = conditionIndex >= 0;
                        const condition = isSelected ? relay.conditions[conditionIndex] : null;
                        
                        return (
                          <TableRow 
                            key={field.field_code}
                            sx={{ 
                              backgroundColor: isSelected ? 'success.light' : 'inherit',
                              transition: 'background-color 0.5s ease',
                              '&:hover': { backgroundColor: isSelected ? 'success.light' : 'action.hover' },
                            }}
                          >
                            <TableCell padding="checkbox" sx={{ textAlign: 'center' }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    const newCond: RelayCondition = {
                                      field: field.field_code,
                                      operator: '<',
                                      setpoint: 25,
                                      hysteresis: 0.5,
                                      group: 1,
                                    };
                                    handleConfigChange(boitierId, relayNum, 'conditions', [...relay.conditions, newCond]);
                                  } else {
                                    const newConditions = relay.conditions.filter(c => c.field !== field.field_code);
                                    handleConfigChange(boitierId, relayNum, 'conditions', newConditions);
                                  }
                                }}
                                color="success"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                                {field.field_name}
                              </Typography>
                              {field.unit && (
                                <Typography variant="caption" color="text.secondary">
                                  ({field.unit})
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {isSelected && condition ? (
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={String(condition.group ?? 1)}
                                    onChange={(e) => {
                                      const newConditions = [...relay.conditions];
                                      newConditions[conditionIndex].group = parseInt(String(e.target.value), 10) || 1;
                                      handleConfigChange(boitierId, relayNum, 'conditions', newConditions);
                                    }}
                                    variant="standard"
                                  >
                                    <MenuItem value="1">1</MenuItem>
                                    <MenuItem value="2">2</MenuItem>
                                    <MenuItem value="3">3</MenuItem>
                                    <MenuItem value="4">4</MenuItem>
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {isSelected && condition ? (
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={condition.operator}
                                    onChange={(e) => {
                                      const newConditions = [...relay.conditions];
                                      newConditions[conditionIndex].operator = e.target.value;
                                      handleConfigChange(boitierId, relayNum, 'conditions', newConditions);
                                    }}
                                    variant="standard"
                                  >
                                    <MenuItem value="<">&lt; inférieur</MenuItem>
                                    <MenuItem value=">">&gt; supérieur</MenuItem>
                                    <MenuItem value="<=">&le; inf. ou égal</MenuItem>
                                    <MenuItem value=">=">&ge; sup. ou égal</MenuItem>
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {isSelected && condition ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  key={`sp-${boitierId}-${relayNum}-${conditionIndex}-${condition.field}`}
                                  defaultValue={condition.setpoint}
                                  onBlur={(e) => {
                                    const val = parseFloat(e.target.value);
                                    if (!isNaN(val)) {
                                      const newConditions = [...relay.conditions];
                                      newConditions[conditionIndex].setpoint = val;
                                      handleConfigChange(boitierId, relayNum, 'conditions', newConditions);
                                    }
                                  }}
                                  variant="standard"
                                  InputProps={{ inputProps: { step: 'any' } }}
                                  sx={{ width: 80 }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {isSelected && condition ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  key={`hy-${boitierId}-${relayNum}-${conditionIndex}-${condition.field}`}
                                  defaultValue={condition.hysteresis}
                                  onBlur={(e) => {
                                    const val = parseFloat(e.target.value);
                                    if (!isNaN(val) && val >= 0) {
                                      const newConditions = [...relay.conditions];
                                      newConditions[conditionIndex].hysteresis = val;
                                      handleConfigChange(boitierId, relayNum, 'conditions', newConditions);
                                    }
                                  }}
                                  variant="standard"
                                  InputProps={{ inputProps: { step: 'any', min: 0 } }}
                                  sx={{ width: 70 }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Résumé lisible: (groupe1) OU (groupe2) */}
                {relay.conditions.length > 0 && (
                  <Paper sx={{ mt: 1.5, p: 1.5, backgroundColor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="bold" color="text.secondary">
                        Relais ON si :
                      </Typography>

                      {(() => {
                        const byGroup = new Map<number, RelayCondition[]>();
                        for (const c of relay.conditions) {
                          const g = Number(c.group ?? 1) || 1;
                          if (!byGroup.has(g)) byGroup.set(g, []);
                          byGroup.get(g)!.push(c);
                        }
                        const groups = Array.from(byGroup.entries()).sort((a, b) => a[0] - b[0]);
                        return groups.map(([gid, conds], gidx) => (
                          <React.Fragment key={gid}>
                            <Chip size="small" label="(" variant="outlined" />
                            {conds.map((cond, cidx) => (
                              <React.Fragment key={`${cond.field}-${cidx}`}>
                                <Chip
                                  size="small"
                                  label={`${fields.find(f => f.field_code === cond.field)?.field_name || cond.field} ${cond.operator} ${cond.setpoint}`}
                                  color="success"
                                  variant="outlined"
                                />
                                {cidx < conds.length - 1 && (
                                  <Chip size="small" label="ET" color="primary" sx={{ height: 20 }} />
                                )}
                              </React.Fragment>
                            ))}
                            <Chip size="small" label=")" variant="outlined" />
                            {gidx < groups.length - 1 && (
                              <Chip size="small" label="OU" color="secondary" sx={{ height: 20 }} />
                            )}
                          </React.Fragment>
                        ));
                      })()}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Dans un groupe : ET. Entre groupes : OU.
                    </Typography>
                  </Paper>
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Options avancées (temporisations en secondes)
                </Typography>
              </Grid>

              {/* Temps min ON */}
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Temps min ON"
                  type="number"
                  value={relay.min_on_time}
                  onChange={(e) => handleConfigChange(boitierId, relayNum, 'min_on_time', parseInt(e.target.value) || 0)}
                  helperText="Sec."
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              {/* Temps min OFF */}
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Temps min OFF"
                  type="number"
                  value={relay.min_off_time}
                  onChange={(e) => handleConfigChange(boitierId, relayNum, 'min_off_time', parseInt(e.target.value) || 0)}
                  helperText="Sec."
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              {/* Temps max ON */}
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Temps max ON"
                  type="number"
                  value={relay.max_on_time || ''}
                  onChange={(e) => handleConfigChange(boitierId, relayNum, 'max_on_time', e.target.value ? parseInt(e.target.value) : null)}
                  helperText="Durée maximale, en secondes"
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>

              {/* Délai avant démarrage */}
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Délai démarrage"
                  type="number"
                  value={relay.delay_before_start}
                  onChange={(e) => handleConfigChange(boitierId, relayNum, 'delay_before_start', parseInt(e.target.value) || 0)}
                  helperText="Sec."
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave(boitierId, relayNum)}
            disabled={isSaving}
          >
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Régulation des relais
      </Typography>

   


      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : relayConfigs.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Aucun boîtier configuré.
        </Alert>
      ) : (
        <Box>
          {relayConfigs.map((config) => (
            <Accordion
              key={config.boitier_id}
              expanded={expanded === config.boitier_id}
              onChange={handleAccordionChange(config.boitier_id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6">
                    {(() => {
                      const boitierInfo = boitiers.find(b => b.boitier_id === config.boitier_id);
                      return boitierInfo?.name
                        ? `Boîtier #${config.boitier_id} - ${boitierInfo.name}`
                        : `Boîtier #${config.boitier_id}`;
                    })()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto', mr: 2 }}>
                    <Chip
                      size="small"
                      label={`R1: ${config.relay1_state ? 'ON' : 'OFF'}`}
                      color={config.relay1_state ? 'success' : 'default'}
                      variant={config.relay1.is_enabled ? 'filled' : 'outlined'}
                    />
                    <Chip
                      size="small"
                      label={`R2: ${config.relay2_state ? 'ON' : 'OFF'}`}
                      color={config.relay2_state ? 'success' : 'default'}
                      variant={config.relay2.is_enabled ? 'filled' : 'outlined'}
                    />
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {renderRelayPanel(config.boitier_id, 1, config.relay1, config.relay1_state, config.fields)}
                {renderRelayPanel(config.boitier_id, 2, config.relay2, config.relay2_state, config.fields)}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegulationView;
